// COUNTRY PROFILES — the single place that describes how each country's
// deployment differs (THAILAND.md §11 / fork governance).
//
// Components must never ask "which country is this?" — they ask the profile:
//   this.$features.singpass          → capability on/off
//   this.$profile.jobsRoutePrefix    → URL wording
//   this.$profile.faqLanguages       → which content languages exist
// Adding a country = adding ONE block here (plus a locale JSON + DB seed);
// no component edits.
//
// The active profile is chosen by the country gate (utils/countryGate.js),
// which applies the double-safety rule — any failure resolves to SG.

export const DEFAULT_COUNTRY = "SG";

export const COUNTRY_PROFILES = {
  SG: {
    locale: "en",
    currency: "S$",
    // ISO 4217 code, for schema.org priceCurrency and anywhere a code is
    // expected rather than the glyph. Mirrors server/server/utils/appCurrency.js.
    currencyCode: "SGD",
    // Bare sign for the copy that says '$' and not 'S$' ("$/Hour", salary
    // ranges). Both conventions exist in SG today and both are preserved, so
    // the currency sweep cannot change SG output; TH collapses them to '฿'.
    currencySign: "$",
    phonePrefix: "+65",
    // IANA zone + fixed UTC offset (neither SG nor TH observes DST).
    // Used by display formatters and schema.org ISO builders (P2 hidden-offset hunt).
    timezone: "Asia/Singapore",
    utcOffset: "+08:00",
    utcOffsetMs: 8 * 60 * 60 * 1000,
    // Mobile input length: SG numbers are 8 digits, no trunk prefix.
    phoneLength: 8,
    // Matches `countries.country_name` in the DB, so address forms can select
    // the right row instead of hardcoding an id. defaultCity only makes sense
    // for a city-state, which is why TH has none.
    countryName: "Singapore",
    // Google Places autocomplete country filter (componentRestrictions).
    placesCountry: "SG",
    defaultCity: "Singapore",
    // First segment of the public job URLs: /singapore-jobs, /singapore-job-ad, ...
    jobsRoutePrefix: "singapore",
    // Job-page label toggle order; first entry is the page default (common/jobLabels.js).
    jobLabelLanguages: [
      { code: "en", label: "English" },
      { code: "zh", label: "中文" },
    ],
    // Gig Missions page app slideshow (right of the download block), in order.
    gigAppSlides: [
      "assets/jobeekerImages/slide-1.webp",
      "assets/jobeekerImages/slide-2.webp",
      "assets/jobeekerImages/slide-3.webp",
    ],
    // FAQ content languages (per-language columns on the faqs table).
    faqLanguages: [
      { value: "en", label: "English" },
      { value: "zh", label: "Chinese" },
      { value: "ms", label: "Malay" },
      { value: "ta", label: "Tamil" },
    ],
    features: {
      singpass: true,
      // Foreign work-pass onboarding (Pass Type/Ploc/Offer Letter/OA docs on the
      // jobseeker profile). Off wherever the country restricts signup to citizens.
      foreignWorkers: true,
    },
  },

  TH: {
    locale: "th",
    currency: "฿",
    currencyCode: "THB",
    currencySign: "฿",
    phonePrefix: "+66",
    timezone: "Asia/Bangkok",
    utcOffset: "+07:00",
    utcOffsetMs: 7 * 60 * 60 * 1000,
    // Thai mobiles are typed as 10 digits with the leading trunk 0
    // (081 234 5678); the server strips the 0 on save (utils/phoneRule.js).
    phoneLength: 10,
    countryName: "Thailand",
    // Google Places autocomplete country filter (componentRestrictions).
    placesCountry: "TH",
    // No default city: Thailand is not a city-state, so guessing "Bangkok"
    // would be wrong for any employer outside it.
    defaultCity: "",
    jobsRoutePrefix: "thailand",
    // Thai National ID is a fixed 13 digits (THAILAND.md §5); SG's NRIC has no
    // fixed max here (unset → no maxlength attribute, matching prior behaviour).
    idLength: 13,
    jobLabelLanguages: [
      { code: "th", label: "ไทย" },
      { code: "en", label: "English" },
    ],
    // Gig Missions page app slideshow (right of the download block), in order.
    gigAppSlides: [
      "assets/jobeekerImages/slide-1-th.webp",
      "assets/jobeekerImages/slide-2-th.webp",
      "assets/jobeekerImages/slide-3-th.webp",
    ],
    // First entry is the FAQ page's default language.
    faqLanguages: [
      { value: "th", label: "ไทย" },
      { value: "en", label: "English" },
    ],
    features: {
      singpass: false,
      // D5: Thai citizens only — foreigners rejected at onboarding (THAILAND.md §5).
      foreignWorkers: false,
    },
  },
};

// Always returns a COMPLETE profile. Unknown/absent country falls back to SG
// (consistent with the gate's fail-safe), and a PARTIAL block inherits any
// missing field from SG — so forgetting a field while adding a country can
// never produce `undefined` at runtime, only SG behaviour for that field.
export function profileFor(country) {
  const base = COUNTRY_PROFILES[DEFAULT_COUNTRY];
  const block = COUNTRY_PROFILES[country];
  if (!block || block === base) return base;
  return Object.assign({}, base, block, {
    features: Object.assign({}, base.features, block.features),
  });
}

// The public jobs homepage path for a country (footer / 404 links).
export function jobsHomePath(country) {
  return "/" + profileFor(country).jobsRoutePrefix + "-jobs";
}

// Router aliases: given the SG path of a job route, the same path for every
// other country's prefix. Used by router/website.js so a new country's URLs
// exist automatically.
export function countryRouteAliases(sgPath) {
  const sgPrefix = "/" + COUNTRY_PROFILES[DEFAULT_COUNTRY].jobsRoutePrefix;
  return Object.keys(COUNTRY_PROFILES)
    .filter((code) => code !== DEFAULT_COUNTRY)
    .map((code) => sgPath.replace(sgPrefix, "/" + COUNTRY_PROFILES[code].jobsRoutePrefix));
}
